import { useEffect } from 'react';
import {
	useQueryClient,
	type InfiniteData,
	type QueryClient,
} from '@tanstack/react-query';
import { useAuthStore } from '@/features/auth';
import { queryKeys } from '@/lib/queryKeys';
import { openRealtime, type RealtimeConnection } from '@/services/realtime';
import type { Display, Page } from '../types';

/** Backend event: a display's connection or "on air" changed. */
export const DISPLAY_STATUS_EVENT = 'display.status';

/** Coalesce bursts (the sweeper may take many displays offline at once). */
const REFRESH_DELAY_MS = 1000;
/** Wait before rejoining after the server closed the socket. */
const REJOIN_DELAY_MS = 5000;

type DisplayStatusEvent = Pick<
	Display,
	| 'connectionStatus'
	| 'lastSeenAt'
	| 'onAir'
	| 'offAirReason'
	| 'onAirChangedAt'
> & { displayId: string };

function isDisplayStatusEvent(value: unknown): value is DisplayStatusEvent {
	const candidate = value as Partial<DisplayStatusEvent> | null;
	return (
		typeof candidate?.displayId === 'string' &&
		(candidate.connectionStatus === 'online' ||
			candidate.connectionStatus === 'offline')
	);
}

function patch(display: Display, event: DisplayStatusEvent): Display {
	return {
		...display,
		connectionStatus: event.connectionStatus,
		lastSeenAt: event.lastSeenAt ?? display.lastSeenAt,
		onAir: event.onAir ?? null,
		offAirReason: event.offAirReason ?? null,
		onAirChangedAt: event.onAirChangedAt ?? null,
	};
}

function hasDisplay(page: Page<Display>, displayId: string): boolean {
	return page.items?.some((display) => display.id === displayId) ?? false;
}

function patchPage(
	page: Page<Display>,
	event: DisplayStatusEvent,
): Page<Display> {
	return {
		...page,
		items: page.items.map((display) =>
			display.id === event.displayId ? patch(display, event) : display,
		),
	};
}

/** What a floor plan caches of a placed display (floor-plans feature). */
type PlanPlacement = {
	displayId: string;
	connectionStatus: Display['connectionStatus'];
	lastSeenAt: string | null;
	onAir: boolean | null;
	offAirReason: string | null;
};

/** Patches the display's marker on a cached floor plan. */
function patchPlan<T extends { placements: PlanPlacement[] }>(
	plan: T | undefined,
	event: DisplayStatusEvent,
): T | undefined {
	if (!plan?.placements?.some((item) => item.displayId === event.displayId)) {
		return plan;
	}
	return {
		...plan,
		placements: plan.placements.map((item) =>
			item.displayId === event.displayId
				? {
						...item,
						connectionStatus: event.connectionStatus,
						lastSeenAt: event.lastSeenAt ?? item.lastSeenAt,
						onAir: event.onAir ?? null,
						offAirReason: event.offAirReason ?? null,
					}
				: item,
		),
	};
}

/** Show the change at once in every cached list and detail of the display. */
function applyDisplayStatus(
	queryClient: QueryClient,
	event: DisplayStatusEvent,
): void {
	queryClient.setQueryData<Display>(
		queryKeys.displays.detail(event.displayId),
		(display) => (display ? patch(display, event) : display),
	);
	queryClient.setQueriesData<InfiniteData<Page<Display>> | Page<Display>>(
		{ queryKey: [...queryKeys.displays.all, 'list'] },
		(data) => {
			if (!data) return data;
			// Infinite lists (tree, search) or one numbered page (table).
			if ('pages' in data) {
				return data.pages.some((page) => hasDisplay(page, event.displayId))
					? {
							...data,
							pages: data.pages.map((page) => patchPage(page, event)),
						}
					: data;
			}
			return hasDisplay(data, event.displayId) ? patchPage(data, event) : data;
		},
	);
	queryClient.setQueriesData<{ placements: PlanPlacement[] }>(
		{ queryKey: [...queryKeys.floorPlans.all, 'plan'] },
		(plan) => patchPlan(plan, event),
	);
}

/**
 * Keeps connection and "on air" live in the CMS
 * (docs/device-online-status-plan.md §2.5): a `display.status` event patches
 * the cached display at once, then the lists, filters and dashboard counts
 * refetch so they agree with the server. Polling stays as the fallback.
 */
export function useDisplayStatusRealtime(): void {
	const accessToken = useAuthStore((state) => state.accessToken);
	const queryClient = useQueryClient();

	useEffect(() => {
		if (!accessToken) return;

		let connection: RealtimeConnection | null = null;
		let refreshTimer: number | null = null;
		let rejoinTimer: number | null = null;
		let closed = false;

		const refresh = () => {
			if (refreshTimer !== null) return;
			refreshTimer = window.setTimeout(() => {
				refreshTimer = null;
				// Filters (online, on air) may now include or drop the display.
				void queryClient.invalidateQueries({
					queryKey: [...queryKeys.displays.all, 'list'],
				});
				void queryClient.invalidateQueries({
					queryKey: queryKeys.dashboard.summary(),
				});
				void queryClient.invalidateQueries({
					queryKey: queryKeys.dashboard.offlineDisplays(),
				});
				void queryClient.invalidateQueries({
					queryKey: queryKeys.dashboard.offAirDisplays(),
				});
			}, REFRESH_DELAY_MS);
		};

		const onStatus = (payload: unknown) => {
			if (!isDisplayStatusEvent(payload)) return;
			applyDisplayStatus(queryClient, payload);
			refresh();
		};

		// The server closes the socket when the token expires; a new token
		// re-runs this effect. Otherwise rejoin after a pause.
		const onServerDisconnect = () => {
			rejoinTimer = window.setTimeout(() => {
				rejoinTimer = null;
				if (!closed) connection?.reconnect();
			}, REJOIN_DELAY_MS);
		};

		void openRealtime(accessToken)
			.then((opened) => {
				if (closed) {
					opened.close();
					return;
				}
				connection = opened;
				opened.on(DISPLAY_STATUS_EVENT, onStatus);
				opened.onServerDisconnect(onServerDisconnect);
			})
			.catch(() => {
				// Realtime is an enhancement; the lists still refresh by polling.
			});

		return () => {
			closed = true;
			if (refreshTimer !== null) window.clearTimeout(refreshTimer);
			if (rejoinTimer !== null) window.clearTimeout(rejoinTimer);
			connection?.close();
		};
	}, [accessToken, queryClient]);
}
